import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { Button, Heading, Paragraph } from '@digdir/designsystemet-react';
import { FilesIcon } from '@navikt/aksel-icons';
import { Routes } from '../../../enums/Routes';
import { buildRoute } from '../../../utils/routeBuilder';
import { daysLeft, TemplateItem } from '../customSurveysView';
import classes from '../CustomSurveyDashboardPage.module.css';

const locale = (language: string) => (language === 'en' ? 'en-GB' : 'nb-NO');

const LiveRow = ({ item }: { item: TemplateItem }) => {
  const { t, i18n } = useTranslation();
  const [copied, setCopied] = useState(false);
  const { template, latest } = item;
  if (!latest) return null;

  const surveyUrl = `${window.location.origin}/custom-survey/${latest.uuid}`;
  const left = latest.deadline !== undefined ? daysLeft(latest.deadline) : undefined;
  const percent = Math.min(100, Math.round(latest.responseRate));

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(surveyUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  return (
    <li className={classes.liveRow}>
      <div className={classes.liveText}>
        <Heading level={3} data-size="2xs">
          {template.name}
        </Heading>
        {latest.deadline && left !== undefined && (
          <span className={classes.deadline} data-soon={left <= 3}>
            {t('customSurveys.dashboard.live.deadline', {
              date: new Date(latest.deadline).toLocaleDateString(locale(i18n.language), { day: 'numeric', month: 'short' }),
              left: left === 0 ? t('customSurveys.dashboard.live.lastDay') : t('customSurveys.dashboard.live.daysLeft', { count: left }),
            })}
          </span>
        )}
        <span className={classes.meta}>
          {t('customSurveys.dashboard.surveys', { count: template.surveysCreated ?? 0 })}
          {template.category && ` · ${template.category}`}
        </span>
      </div>
      <div className={classes.liveProgress}>
        <span className={classes.liveAnswered}>
          <Trans
            i18nKey="customSurveys.dashboard.live.answered"
            values={{ count: latest.responseCount, total: latest.expectedCount }}
            components={[<strong key="0" />]}
          />
        </span>
        <span aria-hidden="true" className={classes.track}>
          <span className={classes.fill} style={{ width: `${percent}%` }} />
        </span>
      </div>
      <div className={classes.liveActions}>
        <Button
          variant="secondary"
          data-size="sm"
          onClick={copyLink}
          aria-label={copied ? undefined : t('customSurveys.dashboard.live.copyFor', { name: template.name })}
          aria-live="polite"
        >
          <FilesIcon aria-hidden />
          {copied ? t('customSurveys.dashboard.live.copied') : t('customSurveys.dashboard.live.copy')}
        </Button>
        <Button asChild variant="tertiary" data-size="sm">
          <a
            href={surveyUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={t('customSurveys.dashboard.live.openFor', { name: template.name })}
          >
            {t('customSurveys.dashboard.live.open')}
          </a>
        </Button>
        <Button asChild variant="tertiary" data-size="sm">
          <Link
            to={buildRoute(Routes.DashboardCustomSurveyTemplateTrends, { templateId: template.id })}
            aria-label={t('customSurveys.dashboard.trendsFor', { name: template.name })}
          >
            {t('customSurveys.dashboard.trends')}
          </Link>
        </Button>
      </div>
    </li>
  );
};

/** Målingene teamet kan svare på nå */
export const LiveList = ({ items }: { items: TemplateItem[] }) => {
  const { t } = useTranslation();
  return (
    <section aria-labelledby="custom-surveys-live" className={classes.section}>
      <div className={classes.liveHeader}>
        <Heading level={3} data-size="xs" id="custom-surveys-live" className={classes.liveTitle}>
          <span aria-hidden="true" className={classes.liveDot} />
          {t('customSurveys.dashboard.live.title')}
        </Heading>
        <Paragraph data-size="sm" className={classes.subtle}>
          {t('customSurveys.dashboard.live.description')}
        </Paragraph>
      </div>
      <ul className={classes.liveList}>
        {items.map((item) => (
          <LiveRow key={item.template.id} item={item} />
        ))}
      </ul>
    </section>
  );
};
